import { useEffect } from 'react';

import { _isNil } from '@/littledash';
import InVivoError from '@/model/InVivoError.ts';
import type { Study, StudyMetrics } from '@/model/Study.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useFetchEntity } from '@/support/Hooks/fetch';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

import { type MetadataSection, useMetadataSections } from '../useMetadataSections';

interface OverviewV2Data {
  benchlingFolderName: string | null;
  completerName: string | null;
  metrics?: StudyMetrics;
  metadataSections: Array<MetadataSection>;
}

export const useOverviewV2Data = (study: Study): OverviewV2Data => {
  const { response: studyInfoResponse } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/info',
    path: { studyApiId: study.api_id },
    options: {
      invokeOnInit: true,
      onError: { toast: false, throw: false, capture: true, slug: 'benchling-folder-name-load' },
    },
  });

  const benchlingFolderName =
    studyInfoResponse?.type === 'success' ? studyInfoResponse.body.benchling_folder_name : null;
  const completerName = studyInfoResponse?.type === 'success' ? studyInfoResponse.body.archived_by_name : null;

  const { entity: metrics, entityError: metricsError } = useFetchEntity<StudyMetrics>({
    entityType: 'studyMetrics',
    params: { id: study.id },
    includes: 'counts',
  });

  useEffect(() => {
    if (_isNil(metricsError)) {
      return;
    }

    ExceptionHandler.captureException(
      new InVivoError('Could not fetch study metrics', {
        cause: metricsError,
        slug: 'study-metrics-load',
      })
    );
  }, [metricsError]);

  const { metadataSections } = useMetadataSections({
    studyMetadata: study.metadata,
    formTemplateId: study.study_creation_template_id,
  });

  return {
    benchlingFolderName,
    completerName,
    metrics,
    metadataSections,
  };
};
